import { getApiBaseUrl } from '@/helpers/helpers';
import i18n from '@/i18n';
import { ApiRequestConfig } from '@/types';
import { ApiError, RefreshTokenResponse } from '@/types/auth';
import { useAuth } from '../zustand/Zustand';
import { Sentry } from '@/helpers/sentry';

class ApiService {
    private baseUrl: string;
    private isRefreshing = false;
    private refreshPromise: Promise<boolean> | null = null;

    constructor() {
        // Updated to use your local ports
        this.baseUrl = getApiBaseUrl();
    }

    private async refreshAccessToken(): Promise<boolean> {
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = this.performTokenRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    private async performTokenRefresh(): Promise<boolean> {
        try {
            const authState = useAuth.getState();
            const { tokens, updateAccessToken, clearAuth } = authState;

            if (!tokens?.refreshToken) {
                clearAuth();
                return false;
            }

            const response = await fetch(`${this.baseUrl}/shared/users/refresh-token`, {
                method: 'POST',
                credentials: 'include', // Important for CORS with cookies
                headers: {
                    'Content-Type': 'application/json',
                    'X-Locale': i18n.language || 'enUS',
                },
                body: JSON.stringify({
                    refreshToken: tokens.refreshToken,
                }),
            });

            if (response.ok) {
                const data: RefreshTokenResponse = await response.json();
                updateAccessToken(data.accessToken);

                return true;
            } else {
                const errorData: ApiError = await response.json();
                Sentry.logger.warn('Token refresh failed', { error: errorData.error });
                clearAuth();
                return false;
            }
        } catch (error) {
            Sentry.logger.warn('Token refresh error', { error });
            const authState = useAuth.getState();
            authState.clearAuth();
            return false;
        }
    }

    private getAuthHeaders(): Record<string, string> {
        const authState = useAuth.getState();
        const { tokens } = authState;

        if (tokens?.accessToken) {
            return {
                'Authorization': `Bearer ${tokens.accessToken}`,
            };
        }

        return {};
    }

    async request(
        endpoint: string,
        config: ApiRequestConfig = {}
    ): Promise<Response> {
        const {
            method = 'GET',
            headers = {},
            body,
            requiresAuth = true,
            retryOnTokenRefresh = true,
        } = config;

        const requestHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Locale': i18n.language || 'enUS',
            ...headers,
        };

        if (requiresAuth) {
            Object.assign(requestHeaders, this.getAuthHeaders());
        }

        const requestConfig: RequestInit = {
            method,
            headers: requestHeaders,
            credentials: 'include', // Essential for CORS with cookies
        };

        if (body && method !== 'GET') {
            requestConfig.body = typeof body === 'string' ? body : JSON.stringify(body);
        }

        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, requestConfig);

            // Handle 401 Unauthorized responses
            if (response.status === 401 && requiresAuth && retryOnTokenRefresh) {
                const refreshSuccess = await this.refreshAccessToken();

                if (refreshSuccess) {
                    // Retry the request with the new token
                    const newHeaders = {
                        ...requestHeaders,
                        ...this.getAuthHeaders(),
                    };

                    const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
                        ...requestConfig,
                        headers: newHeaders,
                    });

                    return retryResponse;
                } else {
                    throw new Error('Authentication failed. Please log in again.');
                }
            }

            return response;
        } catch (error) {
            // Low-level log only: callers catch the rethrown error and report
            // it with the right severity for their flow, so reporting an
            // error here as well would double-count every failure.
            Sentry.logger.warn('API request failed', { endpoint, error: String(error) });
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('An unexpected error occurred', { cause: error });
        }
    }

    // Convenience methods
    async get(endpoint: string, config?: Omit<ApiRequestConfig, 'method'>): Promise<Response> {
        return this.request(endpoint, { ...config, method: 'GET' });
    }

    async post(endpoint: string, body?: string, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<Response> {
        return this.request(endpoint, { ...config, method: 'POST', body });
    }

    async put(endpoint: string, body?: string, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<Response> {
        return this.request(endpoint, { ...config, method: 'PUT', body });
    }

    async delete(endpoint: string, config?: Omit<ApiRequestConfig, 'method'>): Promise<Response> {
        return this.request(endpoint, { ...config, method: 'DELETE' });
    }

    async patch(endpoint: string, body?: string, config?: Omit<ApiRequestConfig, 'method' | 'body'>): Promise<Response> {
        return this.request(endpoint, { ...config, method: 'PATCH', body });
    }
}

// Create and export a singleton instance
const apiService = new ApiService();

// Hook for using the API service in React components
export const getApiService = () => {
    return apiService;
};